import { useState, useEffect, useRef } from "react";

// ─── API ───────────────────────────────────────────────────────────────────────
const API_BASE =
  process.env.REACT_APP_API_BASE || "https://api.seritonex.com";
const token = () => localStorage.getItem("token") || "";
const userId = Number(sessionStorage.getItem("userId") || 0);

const authHeaders = () => ({
  "Content-Type": "application/json",
  accept: "application/json",
  ...(token() ? { Authorization: `Bearer ${token()}` } : {}),
});
async function apiFetch(method, path, body = null) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: authHeaders(),
    ...(body !== null ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return await res.json();
}

const toArray = (data, ...keys) => {
  if (Array.isArray(data)) return data;
  for (const key of keys) {
    const val = data?.[key];
    if (Array.isArray(val)) return val;
    if (val && typeof val === "object") return [val];
  }
  if (data && typeof data === "object" && Object.keys(data).length)
    return [data];
  return [];
};

// ─── API calls ─────────────────────────────────────────────────────────────────
const getBranchCustomers = (user_id) =>
  apiFetch("POST", "/api/v1/amis/branch/by-user-id", { user_id });

const getBranchSuppliers = (branch_id) =>
  apiFetch("POST", "/api/v1/amis/branch/branch-suppliers", { branch_id });

const getWarehousesByBranch = (branch_id) =>
  apiFetch("POST", "/api/v1/amis/warehouse/get-by-branch-id", { branch_id });

const getWarehouseById = (warehouse_id) =>
  apiFetch("POST", "/api/v1/amis/warehouse/get-by-id", { warehouse_id });

const getAssetTypes = (organization_id) =>
  apiFetch("POST", "/api/v1/amis/asset/types", { organization_id });

const createCollection = (payload) =>
  apiFetch("POST", "/api/v1/amis/collection/create", payload);

// ─── Helpers ───────────────────────────────────────────────────────────────────
const optLabel = (item) =>
  item.branch_name ??
  item.name ??
  item.warehouse_name ??
  item.supplier_name ??
  item.customer_name ??
  `ID ${item.branch_id ?? item.id ?? item.warehouse_id ?? "?"}`;

const optValue = (item) =>
  item.branch_id ??
  item.id ??
  item.warehouse_id ??
  item.supplier_id ??
  item.customer_id ??
  "";

// ─── Asset normalisation ───────────────────────────────────────────────────────
// Mirrors CreateOrderPage: extracts the pallet property (property_id === 1) so
// each asset knows whether it ships on pallets and how many cylinders per pallet.
const normalizeAsset = (a) => {
  const props = a.asset_type_properties || [];
  const palletProp = props.find((p) => p.property_id === 1);
  const hasPallet = !!palletProp;
  const cpp = hasPallet ? Number(palletProp.prop_value) || null : null;
  return {
    asset_id: a.asset_type_id,
    asset_type_id: a.asset_type_id,
    type_code: a.type_code ?? "",
    description: a.description ?? "",
    asset_type_properties: props,
    has_pallet: hasPallet,
    cylinders_per_pallet: cpp,
    pallet_asset_type_id: palletProp?.linked_asset_type?.asset_type_id ?? null,
    pallet_description: palletProp?.linked_asset_type?.description ?? null,
  };
};

// ─── Line model ────────────────────────────────────────────────────────────────
const EMPTY_DETAIL = () => ({
  asset_id: "",
  asset_type_id: "",
  asset_type_code: "",
  asset_type_description: "",
  collection_detail_type: "Standard",
  quantity: "", // cylinders — bidirectionally synced with pallets
  pallets: "", // bidirectionally synced with quantity
  has_pallet: false,
  cylinders_per_pallet: null,
  pallet_asset_type_id: null,
});

const DETAIL_TYPES = ["Standard", "Damaged"];

// ════════════════════════════════════════════════════════════════════════════════
const PAL_A_CODE = "PAL-A"; // ← update if the asset code ever changes
// ════════════════════════════════════════════════════════════════════════════════

// ─── Sub-components ────────────────────────────────────────────────────────────
function Field({ label, children, hint }) {
  return (
    <div className="fg">
      <label className="fl">{label}</label>
      {children}
      {hint && (
        <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 4 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

function LoadStatus({ loading, error, count, noun = "results" }) {
  if (loading)
    return (
      <div
        style={{
          fontSize: 10,
          color: "var(--amber)",
          marginTop: 4,
          display: "flex",
          alignItems: "center",
          gap: 5,
        }}
      >
        <span
          style={{
            display: "inline-block",
            width: 10,
            height: 10,
            border: "2px solid var(--amber)",
            borderTopColor: "transparent",
            borderRadius: "50%",
            animation: "spin 0.7s linear infinite",
          }}
        />
        Loading…
      </div>
    );
  if (error)
    return (
      <div style={{ fontSize: 10, color: "var(--danger)", marginTop: 4 }}>
        ⚠ {error}
      </div>
    );
  if (!loading && count === 0)
    return (
      <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 4 }}>
        No {noun} found
      </div>
    );
  return null;
}

// ─── Main Component ────────────────────────────────────────────────────────────
function CreateCollectionPage({
  onBack,
  onCreated,
  showToast,
  isSupplier: _isSupplier,
  userScope,
}) {
  // ── Branch / warehouse state ──────────────────────────────────────────────
  const [customerBranches, setCustomerBranches] = useState([]);
  const [custLoading, setCustLoading] = useState(true);
  const [custError, setCustError] = useState(null);

  const [supplierBranches, setSupplierBranches] = useState([]);
  const [suppLoading, setSuppLoading] = useState(false);
  const [suppError, setSuppError] = useState(null);

  const [warehouses, setWarehouses] = useState([]);
  const [whLoading, setWhLoading] = useState(false);
  const [whError, setWhError] = useState(null);

  // ── Assets ────────────────────────────────────────────────────────────────
  const [assets, setAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [assetsError, setAssetsError] = useState(null);
  const assetsFetchId = useRef(0);

  // ── Form state ────────────────────────────────────────────────────────────
  const [customerBranchId, setCustomerBranchId] = useState("");
  const [supplierBranchId, setSupplierBranchId] = useState("");
  const [branchCustomerId, setBranchCustomerId] = useState(null);
  const [warehouseId, setWarehouseId] = useState("");
  const [collectionNumber, setCollectionNumber] = useState("");
  const [collectionDate, setCollectionDate] = useState("");
  const [address_id, setAddressId] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [region, setRegion] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("");
  const [geoLat, setGeoLat] = useState(null);
  const [geoLng, setGeoLng] = useState(null);
  const [notes, setNotes] = useState("");
  const [details, setDetails] = useState([EMPTY_DETAIL()]);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // ── Fetch customer branches on mount ──────────────────────────────────────
  useEffect(() => {
    if (!userId) {
      setCustError("No user session. Please log in again.");
      setCustLoading(false);
      return;
    }
    getBranchCustomers(userId)
      .then((data) =>
        setCustomerBranches(toArray(data, "branches", "data", "items")),
      )
      .catch((e) => setCustError(e.message))
      .finally(() => setCustLoading(false));
  }, []);

  // ── Customer branch change → load suppliers + warehouses ─────────────────
  function handleCustomerBranchChange(branchId) {
    setCustomerBranchId(branchId);
    setSupplierBranchId("");
    setBranchCustomerId(null); // fixed: was incorrectly reading stale state
    setWarehouseId("");
    setAddressId("");
    setAddressLine1("");
    setAddressLine2("");
    setCity("");
    setRegion("");
    setPostalCode("");
    setCountry("");
    setGeoLat(null);
    setGeoLng(null);
    setSupplierBranches([]);
    setWarehouses([]);
    setAssets([]);
    setSuppError(null);
    setWhError(null);
    setAssetsError(null);
    setDetails([EMPTY_DETAIL()]);
    if (!branchId) return;

    setSuppLoading(true);
    getBranchSuppliers(Number(branchId))
      .then((data) =>
        setSupplierBranches(
          toArray(data, "customers", "suppliers", "branches", "data", "items"),
        ),
      )
      .catch((e) => setSuppError(e.message))
      .finally(() => setSuppLoading(false));

    setWhLoading(true);
    getWarehousesByBranch(Number(branchId))
      .then((data) =>
        setWarehouses(toArray(data, "warehouses", "data", "items")),
      )
      .catch((e) => setWhError(e.message))
      .finally(() => setWhLoading(false));
  }

  // ── Supplier branch change → capture branch_customer_id + fetch assets ───
  function handleSupplierBranchChange(branchId) {
    setSupplierBranchId(branchId);
    setWarehouseId("");
    setAddressId("");
    setAddressLine1("");
    setAddressLine2("");
    setCity("");
    setRegion("");
    setPostalCode("");
    setCountry("");
    setGeoLat(null);
    setGeoLng(null);
    setAssets([]);
    setAssetsError(null);
    setDetails([EMPTY_DETAIL()]);

    const selected = supplierBranches.find(
      (s) => String(optValue(s)) === String(branchId),
    );
    setBranchCustomerId(selected?.branch_customer_id ?? null);
    if (!branchId) return;

    const supplierOrgId = selected?.organization?.organization_id ?? null;
    if (supplierOrgId) {
      const fetchId = ++assetsFetchId.current;
      setAssetsLoading(true);
      getAssetTypes(Number(supplierOrgId))
        .then((data) => {
          if (fetchId !== assetsFetchId.current) return;
          const raw = toArray(data, "assetTypes", "assets", "data", "items");
          setAssets(raw.map(normalizeAsset));
        })
        .catch((e) => {
          if (fetchId !== assetsFetchId.current) return;
          setAssetsError(e.message);
        })
        .finally(() => {
          if (fetchId === assetsFetchId.current) setAssetsLoading(false);
        });
    } else {
      setAssetsError("Supplier has no organization — cannot load asset types.");
    }
  }

  // ── Warehouse change → auto-fill address + geo ────────────────────────────
  async function handleWarehouseChange(wId) {
    setWarehouseId(wId);
    setAddressId("");
    setAddressLine1("");
    setAddressLine2("");
    setCity("");
    setRegion("");
    setPostalCode("");
    setCountry("");
    setGeoLat(null);
    setGeoLng(null);
    if (!wId) return;
    try {
      const data = await getWarehouseById(Number(wId));
      const addr = data?.warehouse?.address ?? {};
      setAddressId(addr.address_id ?? "");
      setAddressLine1(addr.address_line_1 ?? "");
      setAddressLine2(addr.address_line_2 ?? "");
      setCity(addr.city ?? "");
      setRegion(addr.region ?? "");
      setPostalCode(addr.postal_code ?? "");
      setCountry(addr.country ?? "");
      setGeoLat(addr.latitude ?? null);
      setGeoLng(addr.longitude ?? null);
    } catch (e) {
      setWhError(e.message);
    }
  }

  // ── Detail line helpers ───────────────────────────────────────────────────
  const updateDetail = (idx, patch) =>
    setDetails((prev) =>
      prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)),
    );

  // Applies a chosen asset to a line — also populates pallet metadata and resets qty.
  const applyAsset = (idx, picked) => {
    const siblingType = details.find(
      (d, i) => i !== idx && String(d.asset_id) === String(picked.asset_id),
    )?.collection_detail_type;
    const autoType = siblingType
      ? DETAIL_TYPES.find((t) => t !== siblingType)
      : null;
    updateDetail(idx, {
      asset_id: picked.asset_id,
      asset_type_id: picked.asset_type_id,
      asset_type_code: picked.type_code,
      asset_type_description: picked.description,
      has_pallet: picked.has_pallet,
      cylinders_per_pallet: picked.cylinders_per_pallet,
      pallet_asset_type_id: picked.pallet_asset_type_id,
      quantity: "",
      pallets: "",
      ...(autoType && { collection_detail_type: autoType }),
    });
  };

  // Clears all asset and pallet data from a line.
  const clearLineAsset = (idx) =>
    updateDetail(idx, {
      asset_type_code: "",
      asset_id: "",
      asset_type_id: "",
      asset_type_description: "",
      quantity: "",
      pallets: "",
      has_pallet: false,
      cylinders_per_pallet: null,
      pallet_asset_type_id: null,
    });

  // Qty ↔ Pallets are bidirectionally synced for pallet-bearing assets.
  // Editing Qty (cylinders) → Pallets is ALWAYS rounded UP (a partial pallet
  // still occupies a full pallet slot), e.g. 17 cyl @ 16 cpp → 2 pallets.
  const handleQtyChange = (idx, rawVal) => {
    const line = details[idx];
    const cpp = Number(line.cylinders_per_pallet) || 1;
    const qty = Number(rawVal);
    const calcPallets =
      line.has_pallet && qty > 0 ? String(Math.ceil(qty / cpp)) : "";
    updateDetail(idx, { quantity: rawVal, pallets: calcPallets });
  };

  // Editing Pallets (manual entry) → Qty is the exact product, e.g.
  // 2 pallets @ 16 cpp → 32 cyl. Qty remains fully editable afterwards.
  const handlePalletsChange = (idx, rawVal) => {
    const line = details[idx];
    const cpp = Number(line.cylinders_per_pallet) || 1;
    const pallets = Number(rawVal);
    const calcQty = line.has_pallet && pallets > 0 ? String(pallets * cpp) : "";
    updateDetail(idx, { pallets: rawVal, quantity: calcQty });
  };

  const addDetail = () => setDetails((prev) => [...prev, EMPTY_DETAIL()]);
  const removeDetail = (idx) =>
    setDetails((prev) =>
      prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev,
    );

  // ── [PAL-A BLOCK START] ───────────────────────────────────────────────────
  // PAL-A is excluded from all user-selectable dropdowns. Its totals are
  // auto-derived from every pallet-bearing line that links to it.
  // TO REMOVE: delete from here to [PAL-A BLOCK END], then replace every
  // `selectableAssets` reference below with `assets`.
  const palAAsset = assets.find(
    (a) => (a.type_code || "").toUpperCase() === PAL_A_CODE.toUpperCase(),
  );
  const selectableAssets = assets.filter(
    (a) => (a.type_code || "").toUpperCase() !== PAL_A_CODE.toUpperCase(),
  );
  // Sum both cylinders and pallets from pallet-bearing lines whose pallet_asset_type_id
  // points to PAL-A. Each line's pallets value already reflects the round-up rule
  // (or a manual override the user typed directly), so this is a straight sum.
  const palAStats = details.reduce(
    (acc, d) => {
      if (!d.asset_id || !d.has_pallet) return acc;
      if (
        !palAAsset ||
        Number(d.pallet_asset_type_id) !== Number(palAAsset.asset_id)
      )
        return acc;
      return {
        pallets: acc.pallets + (Number(d.pallets) || 0),
        cylinders: acc.cylinders + (Number(d.quantity) || 0),
      };
    },
    { pallets: 0, cylinders: 0 },
  );
  // ── [PAL-A BLOCK END] ─────────────────────────────────────────────────────

  // ── Deduplication helpers ─────────────────────────────────────────────────
  const assetsReady = !assetsLoading && selectableAssets.length > 0;
  const takenKeysByOthers = (lineIdx) =>
    new Set(
      details
        .filter((d, i) => i !== lineIdx && d.asset_id)
        .map((d) => `${d.asset_id}::${d.collection_detail_type}`),
    );
  const availableAssetsForLine = (lineIdx) => {
    const others = takenKeysByOthers(lineIdx);
    return selectableAssets.filter((a) => {
      const allTaken = DETAIL_TYPES.every((t) =>
        others.has(`${a.asset_id}::${t}`),
      );
      return !allTaken;
    });
  };
  const takenKeys = new Set(
    details
      .filter((d) => d.asset_id)
      .map((d) => `${d.asset_id}::${d.collection_detail_type}`),
  );
  const totalSlots = selectableAssets.length * DETAIL_TYPES.length;
  const remainingSlots = totalSlots - takenKeys.size;
  const canAddNewLine = assetsReady && remainingSlots > 0;

  // ── Validation ────────────────────────────────────────────────────────────
  function validate() {
    if (!customerBranchId) return "Please select a customer branch.";
    if (!supplierBranchId) return "Please select a supplier branch.";
    if (!warehouseId) return "Please select a warehouse.";
    if (!collectionDate) return "Please set a collection date.";
    for (const [i, d] of details.entries()) {
      if (!d.asset_type_id)
        return `Line ${i + 1}: please select an asset type.`;
      if (!d.quantity || Number(d.quantity) <= 0)
        return `Line ${i + 1}: quantity must be greater than 0.`;
    }
    return null;
  }

  // ── Submit ────────────────────────────────────────────────────────────────
  async function handleSubmit() {
    const err = validate();
    if (err) {
      showToast(err, "w");
      return;
    }
    setSubmitError(null);
    setSubmitting(true);
    try {
      const payload = {
        collection_number: collectionNumber || undefined,
        customer_branch_id: Number(branchCustomerId) || undefined,
        supplier_branch_id: Number(supplierBranchId) || undefined,
        source_warehouse_id: Number(warehouseId) || undefined,
        collection_date: collectionDate,
        collection_address: Number(address_id) || undefined,
        address_line_1: addressLine1 || undefined,
        address_line_2: addressLine2 || undefined,
        city: city || undefined,
        region: region || undefined,
        postal_code: postalCode || undefined,
        country: country || undefined,
        geo_location: { lat: geoLat, lng: geoLng },
        notes: notes || undefined,
        created_by: userId || undefined,
        details: details.map((d) => ({
          asset_type_id: Number(d.asset_type_id) || undefined,
          asset_type_code: d.asset_type_code || undefined,
          asset_type_description: d.asset_type_description || undefined,
          collection_detail_type:
            d.collection_detail_type === "Damaged" ? 2 : 1,
          quantity: Number(d.quantity) || 0,
          // Include qty_pallets for pallet-bearing assets — rounded up from Qty,
          // or the value the user typed directly into the Pallets field.
          ...(d.has_pallet && d.pallets !== ""
            ? { qty_pallets: Number(d.pallets) }
            : {}),
        })),
      };

      // ── [PAL-A PAYLOAD BLOCK START] ─────────────────────────────────────────
      // PAL-A quantity = total pallets. No Standard/Damaged type (always 1 = Standard).
      // TO REMOVE: delete from here to [PAL-A PAYLOAD BLOCK END].
      if (palAAsset && palAStats.pallets > 0) {
        payload.details.push({
          asset_type_id: Number(palAAsset.asset_type_id),
          asset_type_code: palAAsset.type_code || undefined,
          asset_type_description: palAAsset.description || undefined,
          collection_detail_type: 1, // Standard — always Standard for PAL-A
          quantity: palAStats.pallets,
          qty_pallets: palAStats.pallets,
        });
      }
      // ── [PAL-A PAYLOAD BLOCK END] ───────────────────────────────────────────

      const result = await createCollection(payload);
      showToast("Collection created successfully", "s");
      onCreated?.(result);
    } catch (e) {
      setSubmitError(e.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  // ─── Column layout ─────────────────────────────────────────────────────────
  // Code | Description | Type | Qty (Cylinders) | Pallets | Remove
  const GRID = "1fr 1fr 110px 120px 100px 36px";

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="page">
      {/* Header */}
      <div className="fb mb16">
        <div>
          <div className="ph-title">New Collection</div>
          <div className="hdr-crumb">Collections / New</div>
        </div>
        <button className="back-btn" onClick={onBack}>
          Cancel
        </button>
      </div>

      <div className="panel" style={{ padding: "22px 24px" }}>
        {/* ── Branches ── */}
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: ".1em",
            textTransform: "uppercase",
            color: "var(--text3)",
            marginBottom: 12,
          }}
        >
          Parties
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
            marginBottom: 20,
          }}
        >
          <Field label="Customer Branch">
            <select
              className="fsel"
              value={customerBranchId}
              disabled={custLoading}
              onChange={(e) => handleCustomerBranchChange(e.target.value)}
            >
              <option value="">— Select customer branch —</option>
              {customerBranches.map((c) => (
                <option key={optValue(c)} value={optValue(c)}>
                  {optLabel(c)}
                </option>
              ))}
            </select>
            <LoadStatus
              loading={custLoading}
              error={custError}
              count={customerBranches.length}
              noun="branches"
            />
          </Field>

          <Field label="Supplier Branch">
            <select
              className="fsel"
              value={supplierBranchId}
              disabled={!customerBranchId || suppLoading}
              onChange={(e) => handleSupplierBranchChange(e.target.value)}
            >
              <option value="">
                {!customerBranchId
                  ? "— Select customer branch first —"
                  : "— Select supplier branch —"}
              </option>
              {supplierBranches.map((s) => (
                <option key={optValue(s)} value={optValue(s)}>
                  {optLabel(s)}
                </option>
              ))}
            </select>
            <LoadStatus
              loading={suppLoading}
              error={suppError}
              count={supplierBranches.length}
              noun="branches"
            />
          </Field>

          <Field label="Source Warehouse">
            <select
              className="fsel"
              value={warehouseId}
              disabled={!customerBranchId || whLoading}
              onChange={(e) => handleWarehouseChange(e.target.value)}
            >
              <option value="">
                {!customerBranchId
                  ? "— Select customer branch first —"
                  : "— Select warehouse —"}
              </option>
              {warehouses.map((w) => (
                <option key={optValue(w)} value={optValue(w)}>
                  {optLabel(w)}
                </option>
              ))}
            </select>
            <LoadStatus
              loading={whLoading}
              error={whError}
              count={warehouses.length}
              noun="warehouses"
            />
          </Field>

          <Field
            label="Collection Number"
            hint="Optional — leave blank to auto-generate"
          >
            <input
              className="fi"
              type="text"
              placeholder="e.g. COL-12345"
              value={collectionNumber}
              onChange={(e) => setCollectionNumber(e.target.value)}
            />
          </Field>

          <Field label="Collection Date">
            <input
              className="fi"
              type="date"
              value={collectionDate}
              onChange={(e) => setCollectionDate(e.target.value)}
            />
          </Field>
        </div>

        {/* ── Address (auto-filled from warehouse) ── */}
        {warehouseId && (
          <>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: ".1em",
                textTransform: "uppercase",
                color: "var(--text3)",
                marginBottom: 12,
              }}
            >
              Collection Address
              <span
                style={{
                  fontWeight: 400,
                  textTransform: "none",
                  marginLeft: 6,
                  opacity: 0.6,
                }}
              >
                (auto-filled from warehouse — not editable)
              </span>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
                marginBottom: 20,
              }}
            >
              <Field label="Address Line 1">
                <input
                  className="fi"
                  value={addressLine1}
                  readOnly
                  placeholder="Street address"
                />
              </Field>
              <Field label="Address Line 2">
                <input
                  className="fi"
                  value={addressLine2}
                  readOnly
                  placeholder="Suburb / Unit"
                />
              </Field>
              <Field label="City">
                <input
                  className="fi"
                  value={city}
                  readOnly
                  placeholder="City"
                />
              </Field>
              <Field label="Region / Province">
                <input
                  className="fi"
                  value={region}
                  readOnly
                  placeholder="Province"
                />
              </Field>
              <Field label="Postal Code">
                <input
                  className="fi"
                  value={postalCode}
                  readOnly
                  placeholder="Postal code"
                />
              </Field>
              <Field label="Country">
                <input
                  className="fi"
                  value={country}
                  readOnly
                  placeholder="Country"
                />
              </Field>
            </div>
            {(geoLat != null || geoLng != null) && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "7px 10px",
                  background: "var(--deep, #f5f5f3)",
                  borderRadius: 8,
                  fontFamily: "monospace",
                  fontSize: 11,
                  color: "var(--text2, #666)",
                  marginBottom: 20,
                }}
              >
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: 10,
                    color: "var(--text3, #999)",
                    letterSpacing: ".05em",
                  }}
                >
                  LAT
                </span>
                <span>{geoLat ?? "—"}</span>
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: 10,
                    color: "var(--text3, #999)",
                    letterSpacing: ".05em",
                    marginLeft: 8,
                  }}
                >
                  LNG
                </span>
                <span>{geoLng ?? "—"}</span>
              </div>
            )}
          </>
        )}

        {/* ── Detail Lines ── */}
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: ".1em",
            textTransform: "uppercase",
            color: "var(--text3)",
            marginBottom: 10,
          }}
        >
          Items
          {assetsReady && (
            <span
              style={{
                fontWeight: 400,
                textTransform: "none",
                marginLeft: 8,
                opacity: 0.6,
              }}
            >
              ({selectableAssets.length} asset type
              {selectableAssets.length !== 1 ? "s" : ""} available)
            </span>
          )}
        </div>

        {assetsLoading && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 11,
              color: "var(--amber)",
              marginBottom: 10,
            }}
          >
            <span
              style={{
                display: "inline-block",
                width: 12,
                height: 12,
                border: "2px solid var(--amber)",
                borderTopColor: "transparent",
                borderRadius: "50%",
                animation: "spin 0.7s linear infinite",
              }}
            />
            Loading asset types…
          </div>
        )}

        <div
          style={{
            borderRadius: "var(--r8)",
            border: "1px solid var(--bright)",
            background: "var(--deep)",
            overflow: "hidden",
            marginBottom: 16,
          }}
        >
          {/* ── Header ── */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: GRID,
              gap: 10,
              padding: "8px 14px",
              borderBottom: "1px solid var(--bright)",
              background: "var(--surface)",
            }}
          >
            {[
              "Code",
              "Description",
              "Type",
              "Qty (Cylinders)",
              "Pallets",
              "",
            ].map((h, i) => (
              <div
                key={i}
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: ".1em",
                  textTransform: "uppercase",
                  color: "var(--text3)",
                  textAlign: i >= 3 && i <= 4 ? "right" : "left",
                }}
              >
                {h}
              </div>
            ))}
          </div>

          {/* ── User-editable rows — PAL-A is never selectable here ── */}
          {details.map((d, idx) => {
            const lineAssets = availableAssetsForLine(idx);
            const cpp = Number(d.cylinders_per_pallet) || 1;

            return (
              <div
                key={idx}
                style={{
                  display: "grid",
                  gridTemplateColumns: GRID,
                  gap: 10,
                  padding: "10px 14px",
                  alignItems: "start",
                  borderBottom:
                    idx < details.length - 1
                      ? "1px solid var(--border)"
                      : "none",
                }}
              >
                {/* ── Code dropdown ── */}
                <select
                  className="fsel"
                  value={d.asset_type_code}
                  disabled={assetsLoading}
                  onChange={(e) => {
                    const picked = selectableAssets.find(
                      (a) => a.type_code === e.target.value,
                    );
                    picked ? applyAsset(idx, picked) : clearLineAsset(idx);
                  }}
                >
                  <option value="">— Code —</option>
                  {lineAssets.map((a) => (
                    <option key={a.asset_id} value={a.type_code}>
                      {a.type_code}
                    </option>
                  ))}
                </select>

                {/* ── Description dropdown ── */}
                <select
                  className="fsel"
                  value={d.asset_type_id}
                  disabled={assetsLoading}
                  onChange={(e) => {
                    const picked = selectableAssets.find(
                      (a) => String(a.asset_id) === String(e.target.value),
                    );
                    picked ? applyAsset(idx, picked) : clearLineAsset(idx);
                  }}
                >
                  <option value="">— Description —</option>
                  {lineAssets.map((a) => (
                    <option key={a.asset_id} value={a.asset_id}>
                      {a.description}
                    </option>
                  ))}
                </select>

                {/* ── Type dropdown — Standard / Damaged
                    PAL-A never appears in user rows so every row here has a type ── */}
                <select
                  className="fsel"
                  value={d.collection_detail_type}
                  onChange={(e) => {
                    const newType = e.target.value;
                    const clashExists =
                      d.asset_id &&
                      details.some(
                        (other, i) =>
                          i !== idx &&
                          other.asset_id === d.asset_id &&
                          other.collection_detail_type === newType,
                      );
                    updateDetail(idx, {
                      collection_detail_type: newType,
                      ...(clashExists && {
                        asset_id: "",
                        asset_type_id: "",
                        asset_type_code: "",
                        asset_type_description: "",
                        quantity: "",
                        pallets: "",
                        has_pallet: false,
                        cylinders_per_pallet: null,
                        pallet_asset_type_id: null,
                      }),
                    });
                  }}
                >
                  {DETAIL_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>

                {/* ── Qty (Cylinders) — editable; syncs Pallets via round-up rule ── */}
                <div>
                  <input
                    className="fi"
                    type="number"
                    min={0}
                    placeholder="0"
                    value={d.quantity}
                    disabled={!d.asset_id}
                    onChange={(e) => handleQtyChange(idx, e.target.value)}
                    style={{
                      textAlign: "right",
                      opacity: !d.asset_id ? 0.55 : 1,
                      cursor: !d.asset_id ? "not-allowed" : "text",
                    }}
                  />
                  {d.asset_id && d.has_pallet && (
                    <div
                      style={{
                        fontSize: 9,
                        color: "var(--text3)",
                        marginTop: 3,
                        textAlign: "right",
                      }}
                    >
                      {cpp} cyl/pallet
                    </div>
                  )}
                  {d.asset_id && !d.has_pallet && (
                    <div
                      style={{
                        fontSize: 9,
                        color: "var(--text3)",
                        marginTop: 3,
                        textAlign: "right",
                      }}
                    >
                      direct qty
                    </div>
                  )}
                </div>

                {/* ── Pallets — manually editable; syncs Qty as an exact product ── */}
                <div>
                  <input
                    className="fi"
                    type="number"
                    min={0}
                    placeholder={d.has_pallet ? "0" : "—"}
                    value={d.has_pallet ? d.pallets : ""}
                    disabled={!d.asset_id || !d.has_pallet}
                    onChange={(e) => handlePalletsChange(idx, e.target.value)}
                    style={{
                      textAlign: "right",
                      opacity: !d.asset_id || !d.has_pallet ? 0.55 : 1,
                      cursor:
                        !d.asset_id || !d.has_pallet ? "not-allowed" : "text",
                    }}
                  />
                  {d.asset_id && d.has_pallet && (
                    <div
                      style={{
                        fontSize: 9,
                        color: "var(--text3)",
                        marginTop: 3,
                        textAlign: "right",
                      }}
                    >
                      {cpp} cyl/pallet
                    </div>
                  )}
                  {d.asset_id && !d.has_pallet && (
                    <div
                      style={{
                        fontSize: 9,
                        color: "var(--text3)",
                        marginTop: 3,
                        textAlign: "right",
                      }}
                    >
                      no pallet
                    </div>
                  )}
                </div>

                {/* ── Remove ── */}
                <button
                  onClick={() => removeDetail(idx)}
                  disabled={details.length === 1}
                  title="Remove line"
                  style={{
                    height: 32,
                    width: 32,
                    borderRadius: 6,
                    border: "1px solid var(--bright)",
                    background: "var(--surface)",
                    cursor: details.length === 1 ? "not-allowed" : "pointer",
                    color:
                      details.length === 1
                        ? "var(--text3)"
                        : "var(--danger, #e05260)",
                    fontSize: 14,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: details.length === 1 ? 0.35 : 1,
                    flexShrink: 0,
                    transition: "all .15s",
                  }}
                  onMouseEnter={(e) => {
                    if (details.length > 1) {
                      e.currentTarget.style.background = "rgba(220,53,69,.1)";
                      e.currentTarget.style.borderColor =
                        "var(--danger,#e05260)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "#fff";
                    e.currentTarget.style.borderColor = "var(--bright)";
                  }}
                >
                  🗑
                </button>
              </div>
            );
          })}

          {/* ── [PAL-A UI ROW BLOCK START] ─────────────────────────────────────
              Read-only summary row. Visible whenever PAL-A exists in the loaded
              asset list. PAL-A has no Standard/Damaged type — that cell shows "—".
              Qty (Cylinders) = total cylinders from all pallet-bearing lines.
              Pallets = auto-sum of each line's (rounded-up or manually entered)
              pallet value.
              TO REMOVE: delete from here to [PAL-A UI ROW BLOCK END].           */}
          {palAAsset && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: GRID,
                gap: 10,
                padding: "10px 14px",
                alignItems: "start",
                borderTop: "1px solid var(--border)",
                background: "transparent",
              }}
            >
              {/* Code badge */}
              <div
                style={{
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  padding: "0 10px",
                  borderRadius: 6,
                  border: "1px solid var(--bright)",
                  background: "var(--surface)",
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "var(--text2)",
                    letterSpacing: ".05em",
                  }}
                >
                  {palAAsset.type_code}
                </span>
              </div>

              {/* Description + AUTO badge */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  height: 32,
                  gap: 6,
                  background: "var(--surface)",
                  padding: "0 10px",
                  borderRadius: 6,
                  border: "1px solid var(--bright)",
                }}
              >
                <span style={{ fontSize: 11, color: "var(--text2)" }}>
                  {palAAsset.description}
                </span>
                <span
                  style={{
                    fontSize: 8,
                    fontWeight: 700,
                    color: "var(--text3)",
                    padding: "2px 5px",
                    borderRadius: 3,
                    background: "var(--surface)",
                    letterSpacing: ".08em",
                    textTransform: "uppercase",
                  }}
                >
                  AUTO
                </span>
              </div>

              {/* Type — PAL-A has no Standard/Damaged distinction */}
              <div
                style={{
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  padding: "0 10px",
                  borderRadius: 6,
                  border: "1px solid var(--bright)",
                  background: "var(--surface)",
                  fontSize: 11,
                  color: "var(--text3)",
                }}
              >
                —
              </div>

              {/* Qty (Cylinders) — total cylinders across all pallet-bearing lines */}
              <div>
                <input
                  className="fi"
                  type="number"
                  value={palAStats.cylinders || ""}
                  readOnly
                  disabled
                  placeholder="0"
                  style={{
                    textAlign: "right",
                    background: "var(--surface)",
                    opacity: 1,
                    cursor: "default",
                  }}
                />
                <div
                  style={{
                    fontSize: 9,
                    marginTop: 3,
                    textAlign: "right",
                    fontWeight: 600,
                    color: "var(--text3)",
                  }}
                >
                  {palAStats.cylinders > 0
                    ? `${palAStats.cylinders} cyl total`
                    : "auto-computed"}
                </div>
              </div>

              {/* Pallets — total pallets (sum of each line's pallet value) */}
              <div>
                <input
                  className="fi"
                  type="number"
                  value={palAStats.pallets || ""}
                  readOnly
                  disabled
                  placeholder="0"
                  style={{
                    textAlign: "right",
                    background: "var(--surface)",
                    opacity: 1,
                    cursor: "default",
                  }}
                />
                <div
                  style={{
                    fontSize: 9,
                    marginTop: 3,
                    textAlign: "right",
                    fontWeight: 600,
                    color: "var(--text3)",
                  }}
                >
                  {palAStats.pallets > 0
                    ? `${palAStats.pallets} total`
                    : "auto-computed"}
                </div>
              </div>

              {/* No remove button — PAL-A cannot be deleted */}
              <div />
            </div>
          )}
          {/* ── [PAL-A UI ROW BLOCK END] ──────────────────────────────────── */}

          {/* ── Add Line ── */}
          <div
            style={{
              padding: "8px 14px",
              borderTop: "1px solid var(--border)",
            }}
          >
            <button
              onClick={addDetail}
              disabled={!canAddNewLine}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 12px",
                borderRadius: 6,
                border: `1px dashed ${canAddNewLine ? "var(--flame)" : "var(--bright)"}`,
                background: "var(--surface)",
                color: canAddNewLine ? "var(--flame)" : "var(--text3)",
                fontSize: 11,
                fontWeight: 600,
                cursor: canAddNewLine ? "pointer" : "not-allowed",
                opacity: canAddNewLine ? 1 : 0.5,
              }}
            >
              + Add Line
              {assetsLoading && (
                <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.7 }}>
                  (loading assets…)
                </span>
              )}
              {!assetsLoading &&
                selectableAssets.length === 0 &&
                customerBranchId && (
                  <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.7 }}>
                    (no assets available)
                  </span>
                )}
              {!assetsLoading && assetsReady && remainingSlots === 0 && (
                <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.7 }}>
                  (all asset/type combinations selected)
                </span>
              )}
              {!customerBranchId && (
                <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.7 }}>
                  (select customer branch first)
                </span>
              )}
            </button>
            {assetsError && (
              <div
                style={{ fontSize: 10, color: "var(--danger)", marginTop: 4 }}
              >
                ⚠ {assetsError}
              </div>
            )}
          </div>
        </div>

        {/* Notes */}
        <Field label="Notes (optional)">
          <textarea
            className="fi"
            rows={3}
            placeholder="Any special instructions or notes…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            style={{ resize: "vertical" }}
          />
        </Field>

        {/* Submit error */}
        {submitError && (
          <div
            style={{
              marginTop: 12,
              padding: "10px 14px",
              borderRadius: "var(--r8)",
              background: "rgba(220,53,69,.08)",
              border: "1px solid rgba(220,53,69,.4)",
              fontSize: 12,
              color: "#e05260",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            ⚠️ {submitError}
          </div>
        )}

        {/* Footer */}
        <div
          className="fc"
          style={{
            justifyContent: "space-between",
            marginTop: 24,
            paddingTop: 18,
            borderTop: "1px solid var(--border)",
          }}
        >
          <div
            className="fe"
            style={{
              marginRight: 10,
              padding: "6px 12px",
              borderRadius: 6,
              background: "var(--surface)",
              transition: "all .15s",
            }}
          >
            <button
              className="btn"
              onClick={onBack}
              disabled={submitting}
              style={{ opacity: submitting ? 0.6 : 1 }}
            >
              ← Back to Collections
            </button>
          </div>
          <button
            className="btn btn-p"
            onClick={handleSubmit}
            disabled={submitting}
            style={{ opacity: submitting ? 0.6 : 1 }}
          >
            {submitting ? "Creating…" : "✓ Create Collection"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CreateCollectionPage;
